// ============================================================
// CLUBHUB – iCalendar (RFC 5545): Abo-Adressen, Feed und Einzeltermin
// ============================================================
// Zwei Wege in fremde Kalender:
//
//   ABO     eine Adresse je `CalendarFeed` (persoenlich, Gruppe,
//           oeffentlich), ausgeliefert von `app/api/kalender/[token]`.
//           Der Kalender des Telefons holt sie selbst immer wieder ab —
//           eine geaenderte Trainingszeit steht dort, ohne dass jemand
//           etwas tut. `webcal://` oeffnet auf Apple-Geraeten und in
//           Outlook direkt den Abo-Dialog.
//   DATEI   ein einzelner Termin als .ics zum Herunterladen — fuer
//           Gaeste und alle, die nicht abonnieren wollen.
//
// Zeiten gehen als Ortszeit mit TZID=Europe/Berlin hinaus, dazu der
// VTIMEZONE-Block. Eine Serie ist EIN VEVENT mit RRULE und EXDATE;
// abgesagte oder verschobene Vorkommen folgen als VEVENT mit derselben
// UID und RECURRENCE-ID (RFC 5545, 3.8.4.4). Ganztaegige Termine
// tragen VALUE=DATE, das Ende ist der Tag NACH dem letzten.
//
// FORM: Zeilen enden mit CRLF und sind nach 75 OKTETTEN gefaltet
// (nicht Zeichen — ein Umlaut sind zwei), nie mitten in einem Zeichen.
//
// DATENSCHUTZ: in den Feed kommen Titel, Zeit, Ort, Beschreibung und
// der Link — nie, wer eingeladen ist, zu- oder abgesagt hat.
// ============================================================
import type { CalendarFeedScope } from "@/generated/prisma/enums";
import type { TerminView, VorkommenView } from "./views";
import { addDays, berlinZuUtc, tagVon } from "./tag";

export const ABO_PFAD = "/api/kalender";

/** Die Adressen eines Abos; `host` ohne Schema, z. B. "clubhub.ansichtssache.de". */
export function aboAdresse(token: string, host: string): { https: string; webcal: string; google: string } {
  const https = `https://${host}${ABO_PFAD}/${token}.ics`;
  const webcal = `webcal://${host}${ABO_PFAD}/${token}.ics`;
  return { https, webcal, google: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}` };
}

export const ABO_ART: Record<CalendarFeedScope, { label: string; hinweis: string }> = {
  PERSONAL: { label: "Persönlich", hinweis: "Alle Termine, die dich betreffen" },
  GROUP: { label: "Gruppe", hinweis: "Alle Termine dieser Gruppe" },
  PUBLIC: { label: "Öffentlich", hinweis: "Die öffentlichen Termine, wie auf der Website" },
};

/** Ein Zufallsschluessel fuer eine Abo-Adresse (32 Zeichen, URL-tauglich, rund 160 Bit). */
export function neuerToken(): string {
  const zeichen = "abcdefghijkmnpqrstuvwxyz23456789";
  const zahlen = new Uint8Array(32);
  crypto.getRandomValues(zahlen);
  // 256 ist ein Vielfaches von 32 — keine Schieflage.
  return Array.from(zahlen, (z) => zeichen[z % zeichen.length]).join("");
}

/** Sieht aus wie ein Schluessel aus `neuerToken` (vor jeder Datenbankfrage). */
export function istToken(text: string): boolean {
  return /^[a-z0-9]{24,64}$/.test(text);
}

// ── Bausteine ────────────────────────────────────────────────

/** TEXT-Wert nach RFC 5545, 3.3.11. */
export function icalText(text: string): string {
  return text.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

/** "2026-10-27T19:00:00" → "20261027T190000" */
function lokal(iso: string): string {
  return iso.replace(/[-:]/g, "").slice(0, 15);
}

/** Echter Zeitpunkt → "20261027T180000Z" */
export function utcStempel(d: Date): string {
  return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "").slice(0, 16);
}

const encoder = new TextEncoder();

/** Eine Zeile nach 75 Oktetten falten (RFC 5545, 3.1), ohne ein Zeichen zu zerschneiden. */
export function falten(zeile: string): string {
  if (encoder.encode(zeile).length <= 75) return zeile;
  const teile: string[] = [];
  let aktuell = "";
  let laenge = 0;
  let grenze = 75;
  for (const zeichen of zeile) {
    const n = encoder.encode(zeichen).length;
    if (laenge + n > grenze) {
      teile.push(aktuell);
      aktuell = "";
      laenge = 0;
      // Folgezeilen beginnen mit einem Leerzeichen, das mitzaehlt.
      grenze = 74;
    }
    aktuell += zeichen;
    laenge += n;
  }
  teile.push(aktuell);
  return teile.join("\r\n ");
}

/** Europe/Berlin seit 1996 (EU-Regel: letzter Sonntag im Maerz/Oktober). */
export const VTIMEZONE_BERLIN = [
  "BEGIN:VTIMEZONE",
  "TZID:Europe/Berlin",
  "X-LIC-LOCATION:Europe/Berlin",
  "BEGIN:DAYLIGHT",
  "TZOFFSETFROM:+0100",
  "TZOFFSETTO:+0200",
  "TZNAME:CEST",
  "DTSTART:19700329T020000",
  "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU",
  "END:DAYLIGHT",
  "BEGIN:STANDARD",
  "TZOFFSETFROM:+0200",
  "TZOFFSETTO:+0100",
  "TZNAME:CET",
  "DTSTART:19701025T030000",
  "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU",
  "END:STANDARD",
  "END:VTIMEZONE",
];

/**
 * Die Regel fuer den Feed: DTSTART hat eine Zeitzone, also MUSS UNTIL
 * in UTC stehen (RFC 5545, 3.3.10); bei ganztaegigen Serien als Datum.
 * Gespeichert ist UNTIL als Ortszeit ohne „Z“.
 */
export function regelFuerFeed(rrule: string, ganztaegig: boolean): string {
  return rrule
    .replace(/^RRULE:/i, "")
    .split(";")
    .filter((teil) => teil && !/^DTSTART=/i.test(teil))
    .map((teil) => {
      const [k, v = ""] = teil.split("=");
      if (k!.toUpperCase() !== "UNTIL") return teil;
      const tag = `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
      if (ganztaegig) return `UNTIL=${tag.replaceAll("-", "")}`;
      if (v.endsWith("Z")) return `UNTIL=${v}`;
      const zeit = v.length >= 15 ? `${v.slice(9, 11)}:${v.slice(11, 13)}:${v.slice(13, 15)}` : "23:59:59";
      return `UNTIL=${utcStempel(berlinZuUtc(`${tag}T${zeit}`))}`;
    })
    .join(";");
}

function zeitZeilen(name: "DTSTART" | "DTEND" | "RECURRENCE-ID", iso: string, ganztaegig: boolean): string {
  return ganztaegig ? `${name};VALUE=DATE:${tagVon(iso).replaceAll("-", "")}` : `${name};TZID=Europe/Berlin:${lokal(iso)}`;
}

function beginnUndEnde(start: string, ende: string | null, ganztaegig: boolean): string[] {
  if (ganztaegig) {
    const letzter = tagVon(ende && ende > start ? ende : start);
    return [zeitZeilen("DTSTART", start, true), `DTEND;VALUE=DATE:${addDays(letzter, 1).replaceAll("-", "")}`];
  }
  return [zeitZeilen("DTSTART", start, false), ...(ende ? [zeitZeilen("DTEND", ende, false)] : [])];
}

type Ereignis = {
  uid: string;
  stempel: string;
  titel: string;
  start: string;
  ende: string | null;
  ganztaegig: boolean;
  ort: string | null;
  beschreibung: string | null;
  link: string | null;
  abgesagt: boolean;
  grund: string | null;
  regel?: string | null;
  ausgelassen?: string[];
  recurrenceId?: string | null;
};

function vevent(e: Ereignis): string[] {
  const zeilen = ["BEGIN:VEVENT", `UID:${e.uid}`, `DTSTAMP:${e.stempel}`];
  if (e.recurrenceId) zeilen.push(zeitZeilen("RECURRENCE-ID", e.recurrenceId, e.ganztaegig));
  zeilen.push(...beginnUndEnde(e.start, e.ende, e.ganztaegig));
  if (e.regel) {
    zeilen.push(`RRULE:${regelFuerFeed(e.regel, e.ganztaegig)}`);
    if (e.ausgelassen?.length) {
      zeilen.push(
        e.ganztaegig
          ? `EXDATE;VALUE=DATE:${e.ausgelassen.map((d) => tagVon(d).replaceAll("-", "")).join(",")}`
          : `EXDATE;TZID=Europe/Berlin:${e.ausgelassen.map(lokal).join(",")}`,
      );
    }
  }
  zeilen.push(`SUMMARY:${icalText(e.abgesagt ? `Abgesagt: ${e.titel}` : e.titel)}`);
  if (e.ort) zeilen.push(`LOCATION:${icalText(e.ort)}`);
  const text = [e.abgesagt && e.grund ? `Abgesagt: ${e.grund}` : null, e.beschreibung].filter(Boolean).join("\n\n");
  if (text) zeilen.push(`DESCRIPTION:${icalText(text)}`);
  if (e.link) zeilen.push(`URL:${e.link}`);
  zeilen.push(`STATUS:${e.abgesagt ? "CANCELLED" : "CONFIRMED"}`, "TRANSP:OPAQUE", "END:VEVENT");
  return zeilen;
}

function kalenderText(kopf: string[], koerper: string[]): string {
  return [...kopf, ...VTIMEZONE_BERLIN, ...koerper, "END:VCALENDAR"].map(falten).join("\r\n") + "\r\n";
}

function kopfZeilen(vereinsname: string, name?: string): string[] {
  const zeilen = ["BEGIN:VCALENDAR", "VERSION:2.0", `PRODID:-//clubhub//${icalText(vereinsname)}//DE`, "CALSCALE:GREGORIAN", "METHOD:PUBLISH"];
  if (name) {
    zeilen.push(`X-WR-CALNAME:${icalText(name)}`, "X-WR-TIMEZONE:Europe/Berlin", "REFRESH-INTERVAL;VALUE=DURATION:PT1H", "X-PUBLISHED-TTL:PT1H");
  }
  return zeilen;
}

export type FeedOptionen = {
  vereinsname: string;
  /** Name des Kalenders im Telefon, z. B. „Grünspitz · U13“. */
  name: string;
  termine: TerminView[];
  /** Link zum Termin in der App — `null` fuer oeffentliche Feeds ohne Anmeldung. */
  link: ((key: string) => string) | null;
  /** Domain fuer die UID. */
  uidHost: string;
  jetzt: Date;
};

/** Ein ganzer Kalender (Abo) aus Terminen — Serien bleiben Serien. */
export function icsFeed(o: FeedOptionen): string {
  const stempel = utcStempel(o.jetzt);
  const koerper: string[] = [];
  for (const t of o.termine) {
    const uid = `${t.id}@${o.uidHost}`;
    const abgesagt = Boolean(t.cancelledAt);
    koerper.push(
      ...vevent({
        uid,
        stempel,
        titel: t.title,
        start: t.startsAt,
        ende: t.endsAt,
        ganztaegig: t.allDay,
        ort: t.location,
        beschreibung: t.description,
        link: o.link?.(t.id) ?? null,
        abgesagt,
        grund: t.cancelReason,
        regel: t.rrule,
        ausgelassen: t.exdates,
      }),
    );
    if (!t.rrule) continue;
    const dauer = t.endsAt ? Date.parse(`${t.endsAt}Z`) - Date.parse(`${t.startsAt}Z`) : null;
    for (const a of t.ausnahmen) {
      const start = a.startsAt ?? a.occurrence;
      const ende = a.startsAt ? a.endsAt : dauer === null ? null : new Date(Date.parse(`${a.occurrence}Z`) + dauer).toISOString().slice(0, 19);
      koerper.push(
        ...vevent({
          uid,
          stempel,
          titel: t.title,
          start,
          ende,
          ganztaegig: t.allDay,
          ort: t.location,
          beschreibung: t.description,
          link: o.link?.(`${t.id}~${tagVon(a.occurrence)}`) ?? null,
          abgesagt: abgesagt || a.cancelled,
          grund: a.cancelled ? a.reason : null,
          recurrenceId: a.occurrence,
        }),
      );
    }
  }
  return kalenderText(kopfZeilen(o.vereinsname, o.name), koerper);
}

/** Ein Vorkommen als .ics-Datei; `jetzt` in Ortszeit des Vereins. */
export function icsFuerVorkommen(t: TerminView, v: VorkommenView, vereinsname: string, link: string, jetzt: string, uidHost = "clubhub.ansichtssache.de"): string {
  const koerper = vevent({
    uid: `${v.key}@${uidHost}`,
    stempel: utcStempel(berlinZuUtc(jetzt)),
    titel: v.title,
    start: v.startsAt,
    ende: v.endsAt,
    ganztaegig: v.allDay,
    ort: v.location,
    beschreibung: t.description,
    link,
    abgesagt: v.status === "abgesagt",
    grund: v.reason,
  });
  return kalenderText(kopfZeilen(vereinsname), koerper);
}
